import { toPublicAssetUrl } from './publicAssetUrl';

type TinyMceInit = Record<string, any>;
type TabOption = { id: number; label: string };
type ContainerOption = { id: string; label: string };
type PluginsValue = string[] | string | undefined;
type IconLibrary = 'local' | 'recent' | 'fa' | 'tabler' | 'feather' | 'lucide' | 'ion' | 'simple-icons';

const getTabOptions = (): TabOption[] => {
  if (typeof window === 'undefined') return [];
  const raw = (window as any).__FH_TAB_OPTIONS;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item: any) => {
      if (!item) return null;
      if (typeof item === 'number') {
        return { id: item, label: `tab-${item}` };
      }
      if (typeof item === 'string') {
        const match = item.match(/\d+/);
        if (!match) return null;
        const id = Number.parseInt(match[0], 10);
        if (!Number.isFinite(id)) return null;
        return { id, label: `tab-${id}` };
      }
      const id = Number.parseInt(String(item.id ?? ''), 10);
      if (!Number.isFinite(id)) return null;
      const label = item.label || item.title || `tab-${id}`;
      return { id, label };
    })
    .filter(Boolean) as TabOption[];
};

let cachedLocalIcons: string[] | null = null;
const getLocalIcons = (): string[] => {
  if (cachedLocalIcons) return cachedLocalIcons;
  try {
    // @ts-ignore
    const iconsContext = require.context('../assets/icones', false, /\.(png|jpe?g|svg)$/);
    cachedLocalIcons = iconsContext.keys().map(iconsContext);
  } catch {
    cachedLocalIcons = [];
  }
  return cachedLocalIcons || [];
};

const getContainerOptions = (): ContainerOption[] => {
  if (typeof window === 'undefined') return [];
  const raw = (window as any).__FH_CONTAINER_OPTIONS;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item: any) => {
      if (!item) return null;
      if (typeof item === 'string') {
        const id = item.trim();
        if (!id) return null;
        return { id, label: id };
      }
      if (typeof item === 'number') {
        const id = String(item);
        return { id, label: `container-${id}` };
      }
      const id = String(item.id ?? '').trim();
      if (!id) return null;
      const label = item.label || item.title || id;
      return { id, label };
    })
    .filter(Boolean) as ContainerOption[];
};

const appendToolbarButtons = (toolbar: any, buttons: string[]) => {
  const uniqueButtons = Array.from(new Set(buttons));
  if (!toolbar) return uniqueButtons.join(' ');
  if (typeof toolbar === 'string') {
    const missing = uniqueButtons.filter((button) => !toolbar.includes(button));
    return missing.length ? `${toolbar} | ${missing.join(' ')}` : toolbar;
  }
  if (Array.isArray(toolbar)) {
    const next = [...toolbar];
    uniqueButtons.forEach((button) => {
      const alreadyIncluded = next.some((item) => typeof item === 'string' && item.includes(button));
      if (!alreadyIncluded) next.push(button);
    });
    return next;
  }
  return toolbar;
};

const appendPlugins = (plugins: PluginsValue, extras: string[]) => {
  if (!plugins) return [...extras];
  if (Array.isArray(plugins)) {
    const next = [...plugins];
    extras.forEach((plugin) => {
      if (!next.includes(plugin)) next.push(plugin);
    });
    return next;
  }
  if (typeof plugins === 'string') {
    const parts = plugins.split(/\s+/).filter(Boolean);
    extras.forEach((plugin) => {
      if (!parts.includes(plugin)) parts.push(plugin);
    });
    return parts.join(' ');
  }
  return plugins;
};

const normalizeContextMenu = (contextmenu: unknown, fallback = 'table') => {
  const raw = typeof contextmenu === 'string' ? contextmenu : '';
  const cleanedGroups = raw
    .split('|')
    .map((segment) =>
      segment
        .trim()
        .split(/\s+/)
        .filter((item) => item && !/link/i.test(item))
        .join(' ')
    )
    .filter(Boolean);

  const baseGroups = cleanedGroups.length ? cleanedGroups : [fallback];
  const hasTable = baseGroups.some((group) => /\btable\b/i.test(group));
  const groups = ['cut copy paste', ...baseGroups];

  if (!hasTable) {
    groups.push('table');
  }

  const uniqueGroups = Array.from(new Set(groups.map((group) => group.trim()).filter(Boolean)));
  return uniqueGroups.join(' | ');
};

export const getFlexihubContextMenu = (contextmenu?: string) => normalizeContextMenu(contextmenu, 'table');

const mergeExtendedValidElements = (existing: string | undefined, extra: string) => {
  if (!existing) return extra;
  const hasButton = /button\[/i.test(existing);
  const hasAnchor = /a\[/i.test(existing);
  const parts = [existing];
  if (!hasAnchor) parts.push('a[*]');
  if (!hasButton) parts.push('button[*]');
  return parts.join(',');
};

const iconifyPrefixMap: Record<Exclude<IconLibrary, 'local' | 'recent' | 'fa'>, string> = {
  tabler: 'tabler',
  feather: 'feather',
  lucide: 'lucide',
  ion: 'ion',
  'simple-icons': 'simple-icons'
};

const supportsStroke = (library: IconLibrary) => library === 'tabler' || library === 'feather' || library === 'lucide';

const RECENT_ICONS_KEY = 'flexihub:recent-icons';
const loadRecentIcons = (): Array<{ src: string; size: number }> => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECENT_ICONS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item) => item?.src);
  } catch {
    return [];
  }
};

const saveRecentIcons = (items: Array<{ src: string; size: number }>) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(RECENT_ICONS_KEY, JSON.stringify(items.slice(0, 20)));
  } catch {
    // ignore
  }
};

const addRecentIcon = (icon: { src: string; size: number }) => {
  const existing = loadRecentIcons().filter((item) => item.src !== icon.src);
  saveRecentIcons([icon, ...existing].slice(0, 20));
};

const applyStrokeToSvg = (svg: string, stroke: number) => {
  if (!stroke || stroke <= 0) return svg;
  if (svg.includes('stroke-width=')) {
    return svg.replace(/stroke-width="[^"]*"/, `stroke-width="${stroke}"`);
  }
  return svg.replace('<svg ', `<svg stroke-width="${stroke}" `);
};

const openIconPicker = (editor: any) => {
  const localIcons = getLocalIcons();
  let searchAbort: AbortController | null = null;
  let lastRequest = 0;
  let boundContainer: HTMLElement | null = null;

  const locateDialog = () => {
    const dialogs = Array.from(document.querySelectorAll('.tox-dialog')) as HTMLElement[];
    const root = dialogs.length ? dialogs[dialogs.length - 1] : null;
    const container = (root?.querySelector('#fh-icon-results') ||
      root?.querySelector('[data-fh-icon-results]')) as HTMLElement | null;
    return { root, container };
  };

  const updateVisibility = (root: HTMLElement | null, library: IconLibrary) => {
    if (!root) return;
    const strokeLabel = Array.from(root.querySelectorAll('label')).find((el: any) => el.textContent?.trim() === 'Épaisseur (stroke)');
    const strokeGroup = (strokeLabel as any)?.closest?.('.tox-form__group') || (strokeLabel as any)?.parentElement;
    if (strokeGroup) strokeGroup.setAttribute('style', supportsStroke(library) ? '' : 'display:none');
    const faLabel = Array.from(root.querySelectorAll('label')).find((el: any) => el.textContent?.trim() === 'Style FA');
    const faGroup = (faLabel as any)?.closest?.('.tox-form__group') || (faLabel as any)?.parentElement;
    if (faGroup) faGroup.setAttribute('style', library === 'fa' ? '' : 'display:none');
  };

  const renderResults = (
    container: HTMLElement,
    data: { library: IconLibrary; query: string; color: string; size: number; stroke: number; faStyle: string }
  ) => {
    if (data.library === 'local') {
      container.innerHTML = localIcons.length
        ? localIcons
            .map((url) => `
              <button type="button" data-icon-src="${url}" style="border:1px solid #e9ecef;background:#fff;border-radius:6px;padding:6px;margin:4px;cursor:pointer">
                <img src="${url}" style="width:${data.size}px;height:${data.size}px;object-fit:contain" />
              </button>
            `)
            .join('')
        : '<div style="color:#6c757d">Aucune icône locale.</div>';
      return;
    }

    if (data.library === 'recent') {
      const recent = loadRecentIcons();
      container.innerHTML = recent.length
        ? recent
            .map((item) => `
              <button type="button" data-icon-src="${item.src}" data-icon-size="${item.size}" style="border:1px solid #e9ecef;background:#fff;border-radius:6px;padding:6px;margin:4px;cursor:pointer">
                <img src="${item.src}" style="width:${item.size}px;height:${item.size}px;object-fit:contain" />
              </button>
            `)
            .join('')
        : '<div style="color:#6c757d">Aucune icône récente.</div>';
      return;
    }

    const prefix = data.library === 'fa'
      ? (data.faStyle || 'fa-solid')
      : iconifyPrefixMap[data.library as Exclude<IconLibrary, 'local' | 'recent' | 'fa'>];
    if (!data.query.trim()) {
      container.innerHTML = '<div style="color:#6c757d">Recherchez une icône…</div>';
      return;
    }

    const requestId = ++lastRequest;
    if (searchAbort) searchAbort.abort();
    searchAbort = new AbortController();

    container.innerHTML = '<div style="color:#6c757d">Recherche…</div>';
    const url = `https://api.iconify.design/search?query=${encodeURIComponent(data.query)}&prefix=${encodeURIComponent(prefix)}&limit=60`;
    fetch(url, { signal: searchAbort.signal })
      .then((resp) => resp.json())
      .then((json) => {
        if (requestId !== lastRequest) return;
        const icons: string[] = Array.isArray(json?.icons) ? json.icons : [];
        if (!icons.length) {
          container.innerHTML = '<div style="color:#6c757d">Aucune icône trouvée.</div>';
          return;
        }
        container.innerHTML = icons
          .map((icon) => {
            const iconName = icon.includes(':') ? icon.split(':').pop() || icon : icon;
            const previewUrl = `https://api.iconify.design/${prefix}:${iconName}.svg?color=${encodeURIComponent(data.color)}&width=${data.size}&height=${data.size}`;
            return `
              <button type="button" data-icon-id="${prefix}:${iconName}" style="border:1px solid #e9ecef;background:#fff;border-radius:6px;padding:6px;margin:4px;cursor:pointer">
                <img src="${previewUrl}" style="width:${data.size}px;height:${data.size}px" />
              </button>
            `;
          })
          .join('');
      })
      .catch(() => {
        if (requestId !== lastRequest) return;
        container.innerHTML = '<div style="color:#6c757d">Erreur de recherche.</div>';
      });
  };

  const dialog = editor.windowManager.open({
    title: 'Bibliothèque d’icônes',
    size: 'large',
    body: {
      type: 'panel',
      items: [
        {
          type: 'selectbox',
          name: 'library',
          label: 'Bibliothèque',
          items: [
            { text: 'Locale', value: 'local' },
            { text: 'Icônes récentes', value: 'recent' },
            { text: 'Font Awesome', value: 'fa' },
            { text: 'Tabler', value: 'tabler' },
            { text: 'Feather', value: 'feather' },
            { text: 'Lucide', value: 'lucide' },
            { text: 'Ionicons', value: 'ion' },
            { text: 'Simple Icons', value: 'simple-icons' }
          ]
        },
        {
          type: 'selectbox',
          name: 'faStyle',
          label: 'Style FA',
          items: [
            { text: 'Solid', value: 'fa-solid' },
            { text: 'Regular', value: 'fa-regular' },
            { text: 'Brands', value: 'fa-brands' }
          ]
        },
        { type: 'input', name: 'query', label: 'Recherche' },
        { type: 'input', name: 'size', label: 'Taille (px)' },
        { type: 'colorinput', name: 'color', label: 'Couleur' },
        { type: 'input', name: 'stroke', label: 'Épaisseur (stroke)' },
        { type: 'htmlpanel', name: 'results', html: '<div id="fh-icon-results" data-fh-icon-results="1" style="display:flex;flex-wrap:wrap;max-height:320px;overflow:auto;border:1px solid #e9ecef;padding:6px;border-radius:6px"></div>' }
      ]
    },
    initialData: {
      library: 'local',
      faStyle: 'fa-solid',
      query: '',
      size: '24',
      color: '#0d6efd',
      stroke: '2'
    },
    buttons: [{ type: 'cancel', text: 'Fermer' }],
    onChange: (api: any) => {
      const renderAndBind = () => {
        const data = api?.getData?.() || api || {};
        const library = (data.library || 'local') as IconLibrary;
        const { root, container } = locateDialog();
        if (!container) return false;
        updateVisibility(root, library);
        renderResults(container, {
          library,
          query: String(data.query || ''),
          color: String(data.color || '#0d6efd'),
          size: Math.max(12, Number(data.size || 24)),
          stroke: Math.max(1, Number(data.stroke || 2)),
          faStyle: String(data.faStyle || 'fa-solid')
        });
        if (boundContainer !== container) {
          boundContainer = container;
          container.onclick = async (event) => {
            const target = event.target as Element | null;
            const button = target?.closest?.('button') as HTMLElement | null;
            if (!button) return;
            event.preventDefault();
            const iconSrc = button.getAttribute('data-icon-src');
            const formData = api?.getData?.() || {};
            const iconSize = Number(button.getAttribute('data-icon-size') || formData.size || 24);
            const iconId = button.getAttribute('data-icon-id');
            if (iconSrc) {
              editor.insertContent(`<img src="${iconSrc}" style="width:${iconSize}px;height:${iconSize}px;display:inline-block" />`);
              addRecentIcon({ src: iconSrc, size: iconSize });
              api.close();
              return;
            }
            if (iconId) {
              const size = Math.max(12, Number(formData.size || 24));
              const color = String(formData.color || '#0d6efd');
              const stroke = Math.max(1, Number(formData.stroke || 2));
              const iconUrl = `https://api.iconify.design/${iconId}.svg?color=${encodeURIComponent(color)}&width=${size}&height=${size}`;
              try {
                const resp = await fetch(iconUrl);
                const svg = await resp.text();
                const normalizedSvg = supportsStroke(formData.library as IconLibrary) ? applyStrokeToSvg(svg, stroke) : svg;
                const dataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(normalizedSvg)}`;
                editor.insertContent(`<img src="${dataUrl}" style="width:${size}px;height:${size}px;display:inline-block" />`);
                addRecentIcon({ src: dataUrl, size });
                api.close();
              } catch {
                editor.windowManager.alert('Impossible de charger cette icône.');
              }
            }
          };
        }
        return true;
      };

      renderAndBind();
    },
    onShow: (api: any) => {
      let attempts = 0;
      const refresh = () => {
        attempts += 1;
        const data = api?.getData?.() || {};
        const library = (data.library || 'local') as IconLibrary;
        const { root, container } = locateDialog();
        if (!container) {
          if (attempts < 10) setTimeout(refresh, 50);
          return;
        }
        updateVisibility(root, library);
        renderResults(container, {
          library,
          query: String(data.query || ''),
          color: String(data.color || '#0d6efd'),
          size: Math.max(12, Number(data.size || 24)),
          stroke: Math.max(1, Number(data.stroke || 2)),
          faStyle: String(data.faStyle || 'fa-solid')
        });
        if (boundContainer !== container) {
          boundContainer = container;
          container.onclick = async (event) => {
            const target = event.target as Element | null;
            const button = target?.closest?.('button') as HTMLElement | null;
            if (!button) return;
            event.preventDefault();
            const iconSrc = button.getAttribute('data-icon-src');
            const formData = api?.getData?.() || {};
            const iconSize = Number(button.getAttribute('data-icon-size') || formData.size || 24);
            const iconId = button.getAttribute('data-icon-id');
            if (iconSrc) {
              editor.insertContent(`<img src="${iconSrc}" style="width:${iconSize}px;height:${iconSize}px;display:inline-block" />`);
              addRecentIcon({ src: iconSrc, size: iconSize });
              api.close();
              return;
            }
            if (iconId) {
              const size = Math.max(12, Number(formData.size || 24));
              const color = String(formData.color || '#0d6efd');
              const stroke = Math.max(1, Number(formData.stroke || 2));
              const iconUrl = `https://api.iconify.design/${iconId}.svg?color=${encodeURIComponent(color)}&width=${size}&height=${size}`;
              try {
                const resp = await fetch(iconUrl);
                const svg = await resp.text();
                const normalizedSvg = supportsStroke(formData.library as IconLibrary) ? applyStrokeToSvg(svg, stroke) : svg;
                const dataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(normalizedSvg)}`;
                editor.insertContent(`<img src="${dataUrl}" style="width:${size}px;height:${size}px;display:inline-block" />`);
                addRecentIcon({ src: dataUrl, size });
                api.close();
              } catch {
                editor.windowManager.alert('Impossible de charger cette icône.');
              }
            }
          };
        }
      };

      refresh();
    }
  });

  return dialog;
};

export const withTabLink = (init: TinyMceInit): TinyMceInit => {
  const existingSetup = init.setup;
  const extraValid = 'a[*],button[*]';
  const normalizeFileUrl = (url: string) => toPublicAssetUrl(url) || url;
  const defaultFilePicker = (callback: (url: string, meta?: Record<string, any>) => void, _value: string, meta: any) => {
    const openManager = (window as any).__FH_OPEN_FILE_MANAGER;
    if (typeof openManager !== 'function') {
      return;
    }
    const filetype = meta?.filetype;
    const type = filetype === 'image' ? 'image' : filetype === 'media' ? 'video' : 'all';
    openManager({
      onFileSelect: (file: { url: string }) => {
        if (!file?.url) return;
        const safeUrl = normalizeFileUrl(file.url);
        if (filetype === 'image') {
          callback(safeUrl, { alt: safeUrl.split('/').pop() || 'image' });
          return;
        }
        if (filetype === 'media') {
          callback(safeUrl, { source2: safeUrl, poster: '' });
          return;
        }
        callback(safeUrl);
      }
    });
  };
  const existingFilePicker = init.file_picker_callback;
  const mergedFilePickerTypes = init.file_picker_types
    ? `${init.file_picker_types} image media`
    : 'image media';

  return {
    ...init,
    toolbar: appendToolbarButtons(init.toolbar, ['tablink', 'containerlink', 'iconpicker', 'cloudfile', 'table']),
    plugins: appendPlugins(init.plugins, ['table']),
    table_toolbar: init.table_toolbar || 'tableprops tablecellprops tablecellbackgroundcolor | tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | tablemergecells tablesplitcells',
    contextmenu: getFlexihubContextMenu(init.contextmenu || 'table'),
    extended_valid_elements: mergeExtendedValidElements(init.extended_valid_elements, extraValid),
    file_picker_types: mergedFilePickerTypes,
    file_picker_callback: existingFilePicker || defaultFilePicker,
    setup: (editor: any) => {
      if (typeof existingSetup === 'function') {
        existingSetup(editor);
      }

      if (!editor.ui?.registry?.addButton) return;

      editor.ui.registry.addButton('tablink', {
        text: 'Onglet',
        tooltip: "Insérer un lien vers un onglet du container",
        onAction: () => {
          const tabOptions = getTabOptions();
          const tabItems = tabOptions.map((opt) => ({
            text: opt.label,
            value: String(opt.id)
          }));
          const selectedText = editor.selection?.getContent({ format: 'text' }) || '';
          editor.windowManager.open({
            title: 'Lien vers onglet',
            body: {
              type: 'panel',
              items: [
                {
                  type: 'selectbox',
                  name: 'tabIdSelect',
                  label: 'Onglet',
                  items: tabItems.length ? tabItems : [{ text: 'Aucun onglet disponible', value: '' }]
                },
                { type: 'input', name: 'label', label: 'Texte' },
                {
                  type: 'selectbox',
                  name: 'type',
                  label: 'Type',
                  items: [
                    { text: 'Lien', value: 'link' },
                    { text: 'Bouton', value: 'button' }
                  ]
                }
              ]
            },
            initialData: {
              tabIdSelect: tabItems[0]?.value || '',
              label: selectedText || 'Voir onglet',
              type: 'link'
            },
            buttons: [
              { type: 'cancel', text: 'Annuler' },
              { type: 'submit', text: 'Insérer', primary: true }
            ],
            onSubmit: (api: any) => {
              const data = api.getData() || {};
              const selectedTabId = String(data.tabIdSelect || '').trim();
              const label = editor.dom.encode(String(data.label || 'Voir onglet'));
              const safeTabId = editor.dom.encode(selectedTabId);
              const type = data.type === 'button' ? 'button' : 'link';
              if (!selectedTabId) {
                editor.windowManager.alert('Veuillez sélectionner un onglet.');
                return;
              }

              const allowedIds = tabOptions.map((opt) => String(opt.id));
              if (!allowedIds.includes(selectedTabId)) {
                editor.windowManager.alert('Onglet invalide ou introuvable.');
                return;
              }

              const buttonStyle = 'display:inline-flex;align-items:center;gap:6px;padding:8px 12px;border-radius:999px;border:1px solid #0d6efd;background:#0d6efd;color:#fff;font-weight:600;font-size:0.95rem;cursor:pointer;text-decoration:none;';
              const linkStyle = 'color:#0d6efd;text-decoration:underline;cursor:pointer;';

              if (type === 'button') {
                editor.insertContent(
                  `<button type="button" data-tab-id="${safeTabId}" class="tab-link-btn" style="${buttonStyle}">${label}</button>`
                );
              } else {
                editor.insertContent(
                  `<a href="#" data-tab-id="${safeTabId}" class="tab-link" style="${linkStyle}">${label}</a>`
                );
              }
              api.close();
            }
          });
        }
      });

      editor.ui.registry.addButton('iconpicker', {
        text: 'Icône',
        tooltip: "Insérer une icône",
        onAction: () => {
          openIconPicker(editor);
        }
      });

      editor.ui.registry.addButton('cloudfile', {
        text: 'Cloud',
        tooltip: 'Insérer depuis CloudFileManager',
        onAction: () => {
          const openManager = (window as any).__FH_OPEN_FILE_MANAGER;
          if (typeof openManager !== 'function') {
            editor.windowManager.alert('CloudFileManager indisponible.');
            return;
          }
          editor.windowManager.open({
            title: 'Insérer depuis Cloud',
            body: {
              type: 'panel',
              items: [
                {
                  type: 'selectbox',
                  name: 'mediaType',
                  label: 'Type',
                  items: [
                    { text: 'Image', value: 'image' },
                    { text: 'Vidéo', value: 'video' },
                    { text: 'Lien', value: 'link' }
                  ]
                }
              ]
            },
            initialData: { mediaType: 'image' },
            buttons: [
              { type: 'cancel', text: 'Annuler' },
              { type: 'submit', text: 'Choisir', primary: true }
            ],
            onSubmit: (api: any) => {
              const data = api.getData() || {};
              const mediaType = data.mediaType || 'image';
              api.close();
              openManager({
                onFileSelect: (file: { url: string }) => {
                  const url = file?.url;
                  if (!url) return;
                  const safeUrl = normalizeFileUrl(url);
                  if (mediaType === 'image') {
                    editor.insertContent(`<img src="${safeUrl}" alt="" style="max-width:100%;height:auto;" />`);
                    return;
                  }
                  if (mediaType === 'video') {
                    editor.insertContent(
                      `<video src="${safeUrl}" controls style="max-width:100%;height:auto;"></video>`
                    );
                    return;
                  }
                  editor.insertContent(`<a href="${safeUrl}" target="_blank" rel="noreferrer">${editor.dom.encode(safeUrl)}</a>`);
                }
              });
            }
          });
        }
      });

      editor.ui.registry.addButton('containerlink', {
        text: 'Container',
        tooltip: "Insérer un lien vers un autre container",
        onAction: () => {
          const containerOptions = getContainerOptions();
          const containerItems = containerOptions.map((opt) => ({
            text: opt.label,
            value: opt.id
          }));
          const selectedText = editor.selection?.getContent({ format: 'text' }) || '';
          editor.windowManager.open({
            title: 'Lien vers container',
            body: {
              type: 'panel',
              items: [
                {
                  type: 'selectbox',
                  name: 'containerIdSelect',
                  label: 'Container',
                  items: containerItems.length ? containerItems : [{ text: 'Aucun container disponible', value: '' }]
                },
                { type: 'input', name: 'label', label: 'Texte' },
                {
                  type: 'selectbox',
                  name: 'type',
                  label: 'Type',
                  items: [
                    { text: 'Lien', value: 'link' },
                    { text: 'Bouton', value: 'button' }
                  ]
                }
              ]
            },
            initialData: {
              containerIdSelect: containerItems[0]?.value || '',
              label: selectedText || 'Voir container',
              type: 'link'
            },
            buttons: [
              { type: 'cancel', text: 'Annuler' },
              { type: 'submit', text: 'Insérer', primary: true }
            ],
            onSubmit: (api: any) => {
              const data = api.getData() || {};
              const selectedContainerId = String(data.containerIdSelect || '').trim();
              const label = editor.dom.encode(String(data.label || 'Voir container'));
              const safeContainerId = editor.dom.encode(selectedContainerId);
              const type = data.type === 'button' ? 'button' : 'link';
              if (!selectedContainerId) {
                editor.windowManager.alert('Veuillez sélectionner un container.');
                return;
              }

              const allowedIds = containerOptions.map((opt) => opt.id);
              if (!allowedIds.includes(selectedContainerId)) {
                editor.windowManager.alert('Container invalide ou introuvable.');
                return;
              }

              const buttonStyle = 'display:inline-flex;align-items:center;gap:6px;padding:8px 12px;border-radius:999px;border:1px solid #0d6efd;background:#0d6efd;color:#fff;font-weight:600;font-size:0.95rem;cursor:pointer;text-decoration:none;';
              const linkStyle = 'color:#0d6efd;text-decoration:underline;cursor:pointer;';
              const containerHref = `/container/${encodeURIComponent(selectedContainerId)}`;

              if (type === 'button') {
                editor.insertContent(
                  `<button type="button" data-container-id="${safeContainerId}" class="container-link-btn" style="${buttonStyle}">${label}</button>`
                );
              } else {
                editor.insertContent(
                  `<a href="${containerHref}" data-container-id="${safeContainerId}" class="container-link" style="${linkStyle}">${label}</a>`
                );
              }
              api.close();
            }
          });
        }
      });
    }
  };
};
